import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AnalyticsPeriod, MainCategory } from '../types';
import { formatCurrency, formatDateKey } from '../utils/format';
import { LEGACY_TO_PRIME, PRIME_CATEGORIES } from '../utils/categorization';

interface Props {
  periods: AnalyticsPeriod[];
  mainCategories?: MainCategory[];
  groupBy?: 'category' | 'main';
}

/** Lookup: primeCategoryId → hex color */
const PRIME_COLOR: Record<string, string> = {};
for (const p of PRIME_CATEGORIES) PRIME_COLOR[p.id] = p.color;

/** Distinct color palette for individual categories in detailed view */
const CATEGORY_PALETTE = [
  '#f43f5e', '#fb923c', '#fbbf24', '#a3e635', '#34d399',
  '#22d3ee', '#60a5fa', '#a78bfa', '#f472b6', '#e879f9',
  '#fb7185', '#38bdf8', '#818cf8', '#4ade80', '#facc15',
  '#f97316', '#06b6d4', '#8b5cf6', '#ec4899', '#14b8a6',
  '#ef4444', '#0ea5e9', '#6366f1', '#10b981', '#eab308',
];

/** Simple string hash for deterministic color assignment */
function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Stable unique color for any flat category name */
const _categoryColorCache: Record<string, string> = {};
function categoryColor(catName: string): string {
  if (_categoryColorCache[catName]) return _categoryColorCache[catName];
  // Use hash to pick a distinct color from palette
  const idx = hashString(catName) % CATEGORY_PALETTE.length;
  const color = CATEGORY_PALETTE[idx];
  _categoryColorCache[catName] = color;
  return color;
}

export function SpendingBarChart({
  periods,
  mainCategories = [],
  groupBy = 'category',
}: Props) {
  const { groupedData, barDefs } = useMemo(() => {
    if (groupBy === 'main' && mainCategories.length > 0) {
      // Aggregate all flat categories → prime totals
      const rows = periods.map(period => {
        const row: Record<string, string | number> = { name: formatDateKey(period.date_key) };

        // Prime totals from flat categories
        const primeTotals: Record<string, number> = {};
        for (const [cat, amount] of Object.entries(period.categories)) {
          const primeId = (LEGACY_TO_PRIME[cat] ?? 'others').toLowerCase();
          primeTotals[primeId] = (primeTotals[primeId] ?? 0) + amount;
        }

        // Always write all 5 prime categories (even if 0)
        for (const mainCat of mainCategories) {
          row[mainCat.name] = Math.round((primeTotals[mainCat.id] ?? 0) * 100) / 100;
        }
        return row;
      });

      const bars = mainCategories.map(mc => ({
        key: mc.name,
        color: PRIME_COLOR[mc.id] ?? '#52525b',
      }));

      return { groupedData: rows, barDefs: bars };
    }

    // Category-level view: show every distinct flat category
    const allCategories = Array.from(new Set(periods.flatMap(p => Object.keys(p.categories))));
    const rows = periods.map(period => {
      const row: Record<string, string | number> = { name: formatDateKey(period.date_key) };
      allCategories.forEach(cat => {
        row[cat] = Math.round((period.categories[cat] ?? 0) * 100) / 100;
      });
      return row;
    });

    const bars = allCategories.map(cat => ({
      key: cat,
      color: categoryColor(cat),
    }));

    return { groupedData: rows, barDefs: bars };
  }, [periods, mainCategories, groupBy]);

  if (groupedData.length === 0 || barDefs.length === 0) {
    return <EmptyChart />;
  }

  const isMainView = groupBy === 'main' && mainCategories.length > 0;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={groupedData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fill: '#71717a', fontSize: 11 }}
          axisLine={{ stroke: '#27272a' }}
          tickLine={false}
        />
        <YAxis
          tick={{ fill: '#71717a', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={64}
          tickFormatter={(v: number) => formatCurrency(v)}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#0a0a0a',
            border: '1px solid #27272a',
            borderRadius: 10,
            fontSize: 12,
          }}
          cursor={{ fill: 'rgba(255,255,255,0.03)' }}
          formatter={(value: unknown, name: unknown) => [
            formatCurrency(Number(value)),
            String(name),
          ]}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }}
          iconType="square"
          iconSize={10}
        />
        {barDefs.map((bar) => (
          <Bar
            key={bar.key}
            dataKey={bar.key}
            stackId={isMainView ? undefined : 'spend'}
            fill={bar.color}
            maxBarSize={isMainView ? 56 : 44}
            radius={isMainView ? [3, 3, 0, 0] : [0, 0, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-[300px] items-center justify-center text-sm text-zinc-600">
      No spending data for this timeframe yet — ingest a document to get started.
    </div>
  );
}
